import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Organization, OrgCurrency, OrgUnits, TravelTimeRate, QuoteNumberConfig, StateLicense, ItemizationPreference, ShiftSetting } from '../types/admin';

interface OrgResponse {
  success: boolean;
  data: Organization;
}

export interface OrgSettingsUpdate {
  name?: string;
  logoBase64?: string;
  defaultCurrency?: OrgCurrency;
  defaultUnits?: OrgUnits;
  efficiencyMultiplier?: number;
  defaultTravelTimeRate?: TravelTimeRate;
  fixedTravelTimeHours?: number;
  // Commercial-specific quote defaults
  commercialDefaultTravelTimeRate?: TravelTimeRate;
  commercialFixedTravelTimeHours?: number;
  commercialEfficiencyMultiplier?: number;
  commercialDefaultQuoteValidityDays?: number | null;
  commercialShiftSettings?: ShiftSetting[];
  // Residential-specific quote defaults
  residentialDefaultTravelTimeRate?: TravelTimeRate;
  residentialFixedTravelTimeHours?: number;
  residentialEfficiencyMultiplier?: number;
  residentialDefaultQuoteValidityDays?: number | null;
  residentialShiftSettings?: ShiftSetting[];
  residentialPricingModel?: 'flat_rate' | 'hourly_rate';
  billingEmail?: string;
  legalName?: string;
  businessAddress?: { address: string; placeId: string } | null;
  phone?: string;
  website?: string;
  usdotNumber?: string;
  mcNumber?: string;
  stateLicenses?: StateLicense[];
  quoteFooter?: string;
  quoteNumberConfig?: Partial<QuoteNumberConfig>;
  defaultQuoteValidityDays?: number | null;
  // Feature flags — org admins can toggle within what IOMI has allowed
  featureDispatch?: boolean;
  featureJobs?: boolean;
  featureClientPortal?: boolean;
  featureQuotePdfs?: boolean;
  featureSmartMovingIntegration?: boolean;
  featureCommercialMovingQuotes?: boolean;
  featureResidentialMovingQuotes?: boolean;
  featureLeadPipeline?: boolean;
  featureFleetManagement?: boolean;
  featureStripeConnect?: boolean;
  featureCustomerPayments?: boolean;
  // Rate card settings
  useBlendedTruckDriverRate?: boolean;
  residentialFlatRatesEnabled?: boolean;
  commercialBallparkEnabled?: boolean;
  commercialBallparkMethod?: 'per_employee' | 'per_sqft';
  commercialBallparkRate?: number;
  commercialBallparkRangePercent?: number | null;
  residentialBallparkRangePercent?: number | null;
  commercialItemizationPreference?: ItemizationPreference;
  commercialItemizeMaterials?: boolean;
  commercialItemizeScopeOfServices?: boolean;
  residentialItemizationPreference?: ItemizationPreference;
  residentialItemizeMaterials?: boolean;
  residentialItemizeScopeOfServices?: boolean;
  // Tax calculation
  calculateTaxes?: boolean;
  defaultTaxRate?: number;
  // Local move radius
  localMoveRadiusType?: 'distance' | 'time';
  localMoveRadiusValue?: number;
}

export function useOrgSettings(orgId: string) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OrgResponse>(`/org-admin/${orgId}/settings`);
      setOrg(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load org settings');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(
    async (data: OrgSettingsUpdate) => {
      const res = await api.patch<OrgResponse>(`/org-admin/${orgId}/settings`, data);
      setOrg(res.data);
      return res.data;
    },
    [orgId],
  );

  return { org, setOrg, loading, error, updateSettings, refetch: fetchSettings };
}
